package dev.lingvo.site.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.navigation.UpdateHistoryMode
import dev.lingvo.shared.validateEmail
import dev.lingvo.shared.AcquisitionSource
import dev.lingvo.site.api.ApiException
import dev.lingvo.site.api.AuthProviders
import dev.lingvo.site.api.Session
import dev.lingvo.site.api.SessionState
import dev.lingvo.site.api.rememberSession
import dev.lingvo.site.components.marketing.AuthField
import dev.lingvo.site.components.marketing.AuthHandoff
import dev.lingvo.site.components.marketing.AuthPageScaffold
import dev.lingvo.site.pages.auth.invitationAuthDestinationFromSearch
import dev.lingvo.site.pages.auth.invitationAuthFormPath
import dev.lingvo.site.ds.LDivider
import dev.lingvo.site.components.marketing.GoogleSignInButton
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.Space
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.onSubmit
import org.jetbrains.compose.web.dom.Form
import dev.lingvo.site.ds.rememberSettledWait
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr

@Page
@Composable
fun SignupPage() {
    val ctx = rememberPageContext()
    val scope = rememberCoroutineScope()
    val session = rememberSession()
    val acquisitionSource = AcquisitionSource.fromTag(ctx.route.queryParams["source"])
    val destination = invitationAuthDestinationFromSearch(window.location.search)

    LaunchedEffect(Unit) {
        document.title = tr("signup.create_an_account_lingvo", "Create an account — Lingvo")
    }
    LaunchedEffect(session, destination) {
        if (session is SessionState.Authenticated && window.location.pathname == "/signup") {
            ctx.router.tryRoutingTo(destination, UpdateHistoryMode.REPLACE)
        }
    }

    // **Decide before painting.** `Session.state` starts as `Loading` whenever a token is in
    // storage, so a returning customer who clicks "Start free" used to get the sign-up form held
    // for the whole round trip of `/auth/me` and then a jump to `/app` — asked to create an account
    // they already have, for a second or more. Someone with no token is `LoggedOut` from the first
    // frame and still sees the form immediately, which is the whole point of branching on the
    // session rather than on a timer.
    // Painted only once the wait has outlasted the grace period. A cached session resolves in
    // a frame or two, and this splash rotates a wordmark — appearing and leaving inside 80ms
    // reads as a flicker, not as feedback.
    if (session is SessionState.Loading || session is SessionState.Authenticated) {
        if (rememberSettledWait(active = true)) AuthHandoff(t("signup.checking_your_saved_session", "Checking your saved session."))
        return
    }
    if (session is SessionState.Failure) {
        AuthHandoff(
            message = session.message,
            onRetry = { scope.launch { Session.loadMe() } },
            onUseAnotherAccount = Session::logout,
        )
        return
    }

    var name by remember { mutableStateOf("") }
    var email by remember { mutableStateOf("") }
    var password by remember { mutableStateOf("") }
    var nameTouched by remember { mutableStateOf(false) }
    var emailTouched by remember { mutableStateOf(false) }
    var passwordTouched by remember { mutableStateOf(false) }
    var submitted by remember { mutableStateOf(false) }
    var loading by remember { mutableStateOf(false) }
    var apiError by remember { mutableStateOf<String?>(null) }

    val nameError = if ((submitted || nameTouched) && name.isBlank()) nameRequired() else null
    val emailError = if (submitted || emailTouched) validateEmail(email.trim()) else null
    val passwordError = if (submitted || passwordTouched) validateSignupPassword(password) else null

    fun finishAuthentication() {
        when (val current = Session.state) {
            is SessionState.Authenticated -> {
                if (window.location.pathname == "/signup") {
                    ctx.router.tryRoutingTo(invitationAuthDestinationFromSearch(window.location.search), UpdateHistoryMode.REPLACE)
                }
            }
            is SessionState.Failure -> apiError = current.message
            SessionState.Loading -> apiError = tr(
                "signup.couldnt_finish_loading_your_session_retry",
                "Couldn’t finish loading your session. Retry?",
            )
            SessionState.LoggedOut -> apiError = tr(
                "signup.couldnt_verify_your_session_create_the",
                "Couldn’t verify your session. Create the account again.",
            )
        }
    }

    fun submitSignup() {
        submitted = true
        apiError = null
        val currentNameError = if (name.isBlank()) nameRequired() else null
        val currentEmailError = validateEmail(email.trim())
        val currentPasswordError = validateSignupPassword(password)
        if (currentNameError != null || currentEmailError != null || currentPasswordError != null || loading) return

        loading = true
        scope.launch {
            try {
                Session.register(email.trim(), password, name.trim(), acquisitionSource.tag)
                finishAuthentication()
            } catch (cancelled: CancellationException) {
                throw cancelled
            } catch (failure: ApiException) {
                apiError = failure.message
            } catch (failure: Throwable) {
                apiError = failure.message?.takeIf(String::isNotBlank)
                    ?: tr(
                        "signup.couldnt_create_your_account_check_your",
                        "Couldn’t create your account. Check your connection and retry.",
                    )
            } finally {
                loading = false
            }
        }
    }

    AuthPageScaffold(
        title = t("signup.create_your_workspace", "Create your workspace"),
        subtitle = t(
            "signup.bring_every_key_and_locale_into",
            "Bring every key and locale into one focused translation loop.",
        ),
        alternatePrompt = t("signup.already_have_an_account", "Already have an account?"),
        alternateAction = t("signup.log_in", "Log in"),
        alternatePath = invitationAuthFormPath("/login", destination),
    ) {
        Form(
            attrs = Modifier.fillMaxWidth().toAttrs {
                attr("novalidate", "")
                onSubmit { event ->
                    event.preventDefault()
                    submitSignup()
                }
            },
        ) {
            Column(Modifier.fillMaxWidth().gap(Space.LG)) {
                AuthField(
                    value = name,
                    onValueChange = {
                        name = it
                        apiError = null
                    },
                    label = t("signup.name", "Name"),
                    placeholderText = t("signup.your_name", "Your name"),
                    inputType = InputType.Text,
                    name = "name",
                    autocomplete = "name",
                    error = nameError,
                    enabled = !loading,
                    onBlur = { nameTouched = true },
                    onCommit = ::submitSignup,
                )
                AuthField(
                    value = email,
                    onValueChange = {
                        email = it
                        apiError = null
                    },
                    label = t("signup.email", "Email"),
                    placeholderText = "you@company.com",
                    inputType = InputType.Email,
                    name = "email",
                    autocomplete = "email",
                    error = emailError,
                    enabled = !loading,
                    onBlur = { emailTouched = true },
                    onCommit = ::submitSignup,
                )
                AuthField(
                    value = password,
                    onValueChange = {
                        password = it
                        apiError = null
                    },
                    label = t("signup.password", "Password"),
                    placeholderText = t("signup.at_least_8_characters", "At least 8 characters"),
                    inputType = InputType.Password,
                    name = "password",
                    autocomplete = "new-password",
                    error = passwordError,
                    helperText = t("signup.use_at_least_8_characters", "Use at least 8 characters."),
                    enabled = !loading,
                    onBlur = { passwordTouched = true },
                    onCommit = ::submitSignup,
                )
                if (apiError != null) {
                    LAlert(
                        title = t("signup.couldnt_create_your_account", "Couldn’t create your account"),
                        description = apiError.orEmpty(),
                        modifier = Modifier.fillMaxWidth(),
                        variant = LAlertVariant.DANGER,
                        // It appeared because the reader just pressed "Create account". The field
                        // errors around it already carry `role="alert"`; without this the only
                        // failure never announced is the one the server sent back.
                        live = true,
                    )
                }
                LButton(
                    label = t("signup.create_account", "Create account"),
                    onClick = ::submitSignup,
                    modifier = Modifier.fillMaxWidth(),
                    enabled = !loading,
                    loading = loading,
                )
                // Not while the answer is `false`. `GoogleSignInButton` emits no DOM at all on a
                // deployment without OAuth, so an unconditional rule left the form ending in a
                // separator with nothing under it. `!= false` rather than `== true` on purpose:
                // while the answer is still unknown the button reserves its own height below, and
                // hiding the rule until it lands would put the shift back that reserving removes.
                if (AuthProviders.googleAvailable != false) LDivider(label = "or")
                // Registration and sign-in are the same button: Google does not distinguish them,
                // and neither should we — a returning user who taps "sign up" should land in their
                // workspace, not on an error about an account that already exists.
                GoogleSignInButton(
                    label = t("signup.continue_with_google", "Continue with Google"),
                    redirectTo = destination,
                    enabled = !loading,
                    acquisitionSource = acquisitionSource,
                    modifier = Modifier.fillMaxWidth(),
                )
            }
        }
    }
}

/**
 * The server's password rules, both of them, before a round trip.
 *
 * The byte ceiling is not a second guess at the length one. bcrypt hashes at most 72 bytes and the
 * library `AuthRoutes.validatePassword` uses refuses more, while a character outside ASCII costs two
 * to four of those bytes — so a passphrase, or a short password of accented or CJK characters, used
 * to clear this check, cost a round trip and come back as a server error on the one screen where a
 * mirror exists to spare it.
 */
private fun validateSignupPassword(password: String): String? = when {
    password.isBlank() -> tr("signup.password_is_required", "Password is required.")

    password.length < MIN_PASSWORD_LENGTH ->
        tr("signup.password_must_be_at_least_8", "Password must be at least 8 characters.")

    password.encodeToByteArray().size > MAX_PASSWORD_BYTES ->
        tr(
            "signup.that_password_is_too_long_the",
            "That password is too long. The limit is 72 bytes, and accented or non-Latin " +
                "characters count for more than one byte each.",
        )

    else -> null
}

private fun nameRequired(): String = tr("signup.name_is_required", "Name is required.")

private const val MIN_PASSWORD_LENGTH = 8

/** bcrypt hashes at most 72 bytes; `AuthRoutes.validatePassword` refuses anything longer. */
private const val MAX_PASSWORD_BYTES = 72
